import { ActionContext, GetterTree, MutationTree } from "vuex"
import { getPartSuggestionsForCart } from "@/controllers/library/part"
import { PartSuggestion } from "@/interfaces/library/part"

const CART_SUGGESTIONS_RECEIVED = "CART_SUGGESTIONS_RECEIVED"
const SET_IS_LOADING = "SET_IS_LOADING"
const UPDATE_PART_SUGGESTIONS_QUANTITY = "UPDATE_PART_SUGGESTIONS_QUANTITY"
const GET_MORE_SUGGESTIONS = "GET_MORE_SUGGESTIONS"
const SET_IS_GETTING_MORE = "SET_IS_GETTING_MORE"
const CLEAR_CART_SUGGESTIONS = "CLEAR_CART_SUGGESTIONS"

type State = {
  items: PartSuggestion[],
  pageCount: number,
  pageNumber: number,
  offset: number,
  limit: number,
  total: number,
  isLoading: boolean,
  isGettingMore: boolean,
  partId: number,
  itemsToQty: Map<number, number>
}
type Context = ActionContext<State, any>
type SuggetionTuple = [PartSuggestion, number]
type TupleList = SuggetionTuple[]

const state = getDefaultState()

const getters: GetterTree<State, any> = {
  idToCartSuggestion(state) {
    return new Map(state.items.map(item => [item.id, item]))
  },
  cartSuggestionsWithQuantity(
    state: State,
    getters,
    rootState,
    rootGetters
  ) {
    return state.items.map((item) => {
      return {
        ...item,
        quantity: state.itemsToQty.get(item.id) || 0,
        quantityInCart: rootGetters["cart/itemIdToQty"].get(item.id) ?? 0
      }
    })
  },
  cartSuggestionTuplesToAdd(state: State, getters): TupleList {
    return Array.from(state.itemsToQty.entries())
      .filter(([id, qty]) => state.itemsToQty.has(id) && !!qty)
      .map(([id, qty]) => {
        const suggestion: PartSuggestion = getters.idToCartSuggestion.get(id)
        return [suggestion, qty]
      })
  },
  hasCartSuggestions(state) {
    return state.items.length > 0
  },
  cartSuggestionsForWidgets(
    state: State, 
    getters, 
    rootState, 
    rootGetters
  ) {
    return Array.from(state.itemsToQty.entries())
      .filter(([id, qty]) => state.itemsToQty.has(id) 
        && !!qty 
        && rootGetters["widgets/isWidgetCartEnabled"]
      ).map(([id, qty]) => {
        const item: PartSuggestion = getters.idToCartSuggestion.get(id)
        return [{
          widgetType: rootGetters["widgets/widgetType"],
          partId: item.id,
          partDescription: item.description,
          partName: item.name,
          partNumber: item.partNumber,
          quantity: qty,
          supplierKey: item.supplierKey,
          uom: item.uom
        }]
      })
  },
  hasCartSuggestionsToAdd(state, getters) {
    return !!getters.cartSuggestionTuplesToAdd.length
  }
}

const actions = {
  async getCartSuggestions(
    { commit, state, rootGetters }: Context,
    partId: number
  ) {
    try {
      if (!state.isLoading && rootGetters["user/hasPartOrderSuggestions"]) {
        commit(SET_IS_LOADING, true)
        const params = {
          limit: state.limit,
          offset: state.offset
        }

        const { headers, data } = await getPartSuggestionsForCart(partId, params)
        commit(CART_SUGGESTIONS_RECEIVED, { suggestions: data, meta: headers, partId })
      }
    } catch (err) {
      console.error('error :>> ', err)
      commit(CART_SUGGESTIONS_RECEIVED, { suggestions: [], meta: { 'x-count': 0 } })
    } finally {
      commit(SET_IS_LOADING, false)
    }
  },
  async getMoreCartSuggestions({ state, rootGetters, commit }: Context) {
    const hasAccess = rootGetters["user/hasPartOrderSuggestions"]
    const hasMoreToFetch = state.pageCount > state.pageNumber

    if (hasAccess && hasMoreToFetch && !state.isLoading && !state.isGettingMore) {
      try {
        commit(SET_IS_GETTING_MORE, true)
        const params = {
          limit: state.limit,
          offset: state.offset
        }
        const { headers, data } = await getPartSuggestionsForCart(state.partId, params)
        commit(GET_MORE_SUGGESTIONS, { suggestions: data, meta: headers })
      } catch (err) {
        console.error('error :>> ', err)
      } finally {
        commit(SET_IS_GETTING_MORE, false)
      }
    }
  },
  updateCartSuggestionQuantity(
    { commit }: Context, 
    { partId, quantity }: { partId: number, quantity: number }
  ) {
    commit(UPDATE_PART_SUGGESTIONS_QUANTITY, { partId, quantity })
  },
  clearCartSuggestions({ commit }: Context) {
    commit(CLEAR_CART_SUGGESTIONS)
  },
  async addSuggestionsToCart({ dispatch, getters }: Context) {
    const tupleList: TupleList = getters.cartSuggestionTuplesToAdd
    const dispatches = tupleList.map(tuple => (
      dispatch(
        'cart/addToCart', 
        { part: tuple[0], qty: tuple[1] }, 
        { root: true }
      )
    ))
    await Promise.all(dispatches)
  }
}

const mutations: MutationTree<State> = {
  [CART_SUGGESTIONS_RECEIVED](state, { suggestions, meta, partId }) {
    state.items = suggestions
    state.total = parseInt(meta['x-count'] ?? 0)
    state.pageCount = parseInt(meta['x-page-count'] ?? 0)
    state.pageNumber = parseInt(meta['x-page-num'] ?? 0)
    state.offset = parseInt(meta['x-offset'] ?? 0) + state.limit
    state.isLoading = false
    state.partId = partId
  },
  [GET_MORE_SUGGESTIONS](state, { suggestions, meta }) {
    state.items = [...state.items, ...suggestions]
    state.total = parseInt(meta['x-count'] ?? 0)
    state.pageCount = parseInt(meta['x-page-count'] ?? 0)
    state.pageNumber = parseInt(meta['x-page-num'] ?? 0)
    state.offset = parseInt(meta['x-offset'] ?? 0) + state.limit
    state.isGettingMore = false
  },
  [SET_IS_LOADING] (state, payload: boolean) {
    state.isLoading = payload
  },
  [SET_IS_GETTING_MORE] (state, payload: boolean) {
    state.isGettingMore = payload
  },
  [UPDATE_PART_SUGGESTIONS_QUANTITY] (
    state, 
    { partId, quantity }: { partId: number, quantity: number }
  ) {
    if (quantity === 0) {
      state.itemsToQty.delete(partId)
    } else {
      state.itemsToQty.set(partId, quantity)
    }
  }, 
  [CLEAR_CART_SUGGESTIONS] (state) {
    Object.assign(state, getDefaultState())
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}

function getDefaultState() {
  return {
    items: [],
    offset: 0,
    limit: 20,
    total: 0,
    pageCount: 0,
    pageNumber: 0,
    partId: null,
    itemsToQty: new Map(),
    isLoading: false
  }
}